// services/PostcodeService.ts

import { PostcodeLocation, PostcodeValidationResult, DeliveryValidationResult } from '../types/postcode';

class PostcodeService {
  private static instance: PostcodeService;
  private postcodeCache: Map<string, PostcodeLocation> = new Map();
  
  private constructor() {}

  public static getInstance(): PostcodeService {
    if (!PostcodeService.instance) {
      PostcodeService.instance = new PostcodeService();
    }
    return PostcodeService.instance;
  }

  private async fetchPostcodeData(postcode: string): Promise<PostcodeLocation | null> {
    // Check cache first
    const cachedData = this.postcodeCache.get(postcode);
    if (cachedData) return cachedData;

    try {
      const response = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error('Failed to fetch postcode data');
      }

      const data = await response.json();
      const postcodeData = {
        postcode: data.result.postcode,
        latitude: data.result.latitude,
        longitude: data.result.longitude,
        region: data.result.region,
        outcode: data.result.outcode
      };

      // Cache the result
      this.postcodeCache.set(postcode, postcodeData);
      return postcodeData;
    } catch (error) {
      console.error('Error fetching postcode:', error);
      return null;
    }
  }

  public async validatePostcode(postcode: string): Promise<PostcodeValidationResult> {
    const formattedPostcode = postcode.replace(/\s+/g, ' ').trim().toUpperCase();
    const postcodeData = await this.fetchPostcodeData(formattedPostcode);

    if (!postcodeData) {
      console.log("invalid postcode entered...")
      return {
        isValid: false,
        message: 'Please enter a valid UK postcode'
      };
    }

    return {
      isValid: true,
      location: postcodeData
    };
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);
    const lat1Rad = this.toRad(lat1);
    const lat2Rad = this.toRad(lat2);

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1Rad) * Math.cos(lat2Rad);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c * 10) / 10; // Round to 1 decimal place
  }

  private toRad(value: number): number {
    return value * Math.PI / 180;
  }

  private estimateDeliveryTime(distance: number): number {
    const baseTime = 20;
    const travelTime = Math.ceil(distance * 4);
    return baseTime + travelTime;
  }

  public async validateDeliveryLocation(
    userPostcode: string, 
    restaurantPostcodes: string[],
    radius?: number,
    rlatitude?: string,
    rlongitude?: string,
  ): Promise<DeliveryValidationResult> {
    const validation = await this.validatePostcode(userPostcode);
    if (!validation.isValid || !validation.location) {
      return {
        invalidLocation: true,
        canDeliver: false,
        message: validation.message || 'Invalid postcode',
        distance: 0,
      };
    }

    let shortestDistance = Infinity;
    let nearestPostcode = '';
    let foundValidLocation = false;

    console.log("postcode Service", rlongitude)

    if (rlatitude) {
      console.log("LAT",        validation.location.latitude,
        validation.location.longitude,
        Number(rlatitude),
        Number(rlongitude) )
      const trueDistance = this.calculateDistance(
        validation.location.latitude,
        validation.location.longitude,
        Number(rlatitude),
        Number(rlongitude)   
      )

      if (trueDistance < shortestDistance) {
        shortestDistance = trueDistance;
        foundValidLocation = true;
      }

      console.log("true calculated distance is: ", trueDistance)

    }

    else {
      // Check each restaurant location
      for (const restaurantPostcode of restaurantPostcodes) {
        const locationData = await this.fetchPostcodeData(restaurantPostcode);
        if (!locationData) continue;

        const distance = this.calculateDistance(
          validation.location.latitude,
          validation.location.longitude,
          locationData.latitude,
          locationData.longitude
        );

        if (distance < shortestDistance) {
          shortestDistance = distance;
          nearestPostcode = restaurantPostcode;
          foundValidLocation = true;
        }
      }

    }

    if (!foundValidLocation) {
      return {
        canDeliver: false,
        message: 'Restaurant location not available',
        distance: 0,
      };
    }

    // Always return the distance, even if it's large
    return {
      canDeliver: radius ? (shortestDistance <= radius) : (shortestDistance <= 5.001),
      distance: shortestDistance,
      nearestLocation: nearestPostcode || ""
    };
  }
}

export default PostcodeService;